/**
 * Guide registry — the single place guides are defined.
 *
 * A guide is a list of steps the overlay walks the user through. Pages take
 * part only by adding `data-guide-target="<name>"` to the element a step
 * should highlight; they carry no guide logic of their own.
 *
 * Shape:
 *   {
 *     id: 'create_team',                   // stable, matches the catalogue
 *     nameKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NAME', // shown on the card
 *     doneKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DONE', // the result line on
 *                                          // the "done" card shown when the
 *                                          // guide finishes
 *     descriptionKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DESCRIPTION', // the
 *                                          // line on the onboarding list
 *     tier: 2,                             // catalogue tier, 1 to 4; tier 2
 *                                          // guides are optional extras
 *     finishRoutes: ['settings_teams_finish'], // optional: arriving on one of
 *                                          // these ends the guide as finished
 *     steps: [
 *       {
 *         id: 'new-button',                // unique within the guide
 *         routes: ['settings_teams_list'], // route names the step is shown on
 *         routeParams: { sub_page: 'website' }, // optional: params the route
 *                                          // must also have (a route name
 *                                          // that serves many pages)
 *         target: 'teams-new-button',      // data-guide-target value (optional:
 *                                          // no target = centred card)
 *         titleKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NEW_BUTTON.TITLE',
 *         bodyKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NEW_BUTTON.BODY',
 *         placement: 'bottom',             // top | bottom | left | right (optional)
 *         action: true,                    // optional: the user does this on
 *                                          // the page, so the card has no
 *                                          // Next (the guide moves on when
 *                                          // they do)
 *         disabledKey: '...DISABLED',      // optional: shown when the target
 *                                          // is a disabled button, saying
 *                                          // what unlocks it
 *         navigable: false,                // optional: don't push the user to
 *                                          // this step's page on Next/Back
 *                                          // (it needs an id from their own
 *                                          // action)
 *       },
 *     ],
 *   }
 *
 * Button names inside copy are linked messages to the same i18n key the page
 * uses (`@:(SIDEBAR.NEW_INBOX)`), so the card can never name a button
 * differently from the page.
 *
 * While a guide runs, the user moving to a page that belongs to a later step
 * jumps the guide forward to that step, so "click New team" advances by
 * itself once they do.
 *
 * Completion stays manual: finishing or exiting a guide only clears the
 * overlay; the user marks the guide done from the onboarding list.
 */
export const PLACEMENTS = ['top', 'bottom', 'left', 'right'];

const keys = (guide, step) => ({
  titleKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.TITLE`,
  bodyKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.BODY`,
});

const withDisabled = (guide, step) => ({
  ...keys(guide, step),
  disabledKey: `ONBOARDING_GUIDE.GUIDES.${guide}.${step}.DISABLED`,
});

const CREATE_TEAM = {
  id: 'create_team',
  nameKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.NAME',
  doneKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DONE',
  descriptionKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_TEAM.DESCRIPTION',
  tier: 2,
  finishRoutes: ['settings_teams_finish'],
  steps: [
    {
      id: 'new-button',
      routes: ['settings_teams_list'],
      target: 'teams-new-button',
      placement: 'bottom',
      action: true,
      ...keys('CREATE_TEAM', 'NEW_BUTTON'),
    },
    {
      id: 'name',
      routes: ['settings_teams_new'],
      target: 'team-form-name',
      placement: 'right',
      ...keys('CREATE_TEAM', 'NAME_FIELD'),
    },
    {
      id: 'submit',
      routes: ['settings_teams_new'],
      target: 'team-form-submit',
      placement: 'top',
      action: true,
      ...withDisabled('CREATE_TEAM', 'SUBMIT'),
    },
    {
      id: 'agents',
      routes: ['settings_teams_add_agents'],
      navigable: false,
      target: 'team-agent-selector',
      placement: 'top',
      action: true,
      ...keys('CREATE_TEAM', 'AGENTS'),
    },
  ],
};

const CREATE_WEBSITE_INBOX = {
  id: 'create_website_inbox',
  nameKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_WEBSITE_INBOX.NAME',
  doneKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_WEBSITE_INBOX.DONE',
  descriptionKey: 'ONBOARDING_GUIDE.GUIDES.CREATE_WEBSITE_INBOX.DESCRIPTION',
  tier: 1,
  finishRoutes: ['settings_inbox_finish'],
  steps: [
    {
      id: 'new-button',
      routes: ['settings_inbox_list'],
      target: 'inbox-new-button',
      placement: 'bottom',
      action: true,
      ...keys('CREATE_WEBSITE_INBOX', 'NEW_BUTTON'),
    },
    {
      id: 'choose-website',
      routes: ['settings_inbox_new'],
      target: 'channel-website',
      placement: 'bottom',
      action: true,
      ...keys('CREATE_WEBSITE_INBOX', 'CHOOSE_WEBSITE'),
    },
    {
      id: 'name',
      routes: ['settings_inboxes_page_channel'],
      routeParams: { sub_page: 'website' },
      target: 'website-name',
      placement: 'right',
      ...keys('CREATE_WEBSITE_INBOX', 'NAME_FIELD'),
    },
    {
      id: 'domain',
      routes: ['settings_inboxes_page_channel'],
      routeParams: { sub_page: 'website' },
      target: 'website-domain',
      placement: 'right',
      ...keys('CREATE_WEBSITE_INBOX', 'DOMAIN'),
    },
    {
      id: 'submit',
      routes: ['settings_inboxes_page_channel'],
      routeParams: { sub_page: 'website' },
      target: 'website-submit',
      placement: 'top',
      action: true,
      ...withDisabled('CREATE_WEBSITE_INBOX', 'SUBMIT'),
    },
    {
      id: 'platform',
      routes: ['settings_inboxes_choose_platform'],
      navigable: false,
      target: 'platform-confirm',
      placement: 'top',
      action: true,
      ...keys('CREATE_WEBSITE_INBOX', 'PLATFORM'),
    },
    {
      id: 'agents',
      routes: ['settings_inboxes_add_agents'],
      navigable: false,
      target: 'inbox-agents-submit',
      placement: 'top',
      action: true,
      ...keys('CREATE_WEBSITE_INBOX', 'AGENTS'),
    },
  ],
};

// Guides are added here as they are built (pilots first, then by tier).
export const GUIDES = {
  [CREATE_TEAM.id]: CREATE_TEAM,
  [CREATE_WEBSITE_INBOX.id]: CREATE_WEBSITE_INBOX,
};

export const getGuide = guideId => GUIDES[guideId] || null;

// Tier 2 of the catalogue is "optional onboarding features".
export const OPTIONAL_TIER = 2;
export const isOptionalGuide = guide => guide.tier === OPTIONAL_TIER;

/**
 * Returns a list of problems with a guide definition (empty when valid).
 * Used by the registry spec so a malformed guide fails CI rather than
 * silently rendering nothing.
 */
export const validateGuide = guide => {
  const problems = [];
  if (!guide || typeof guide.id !== 'string' || !guide.id) {
    return ['guide needs a string id'];
  }
  if (!Array.isArray(guide.steps) || guide.steps.length === 0) {
    return [`${guide.id}: needs at least one step`];
  }
  if (typeof guide.nameKey !== 'string' || !guide.nameKey) {
    problems.push(`${guide.id}: needs a nameKey`);
  }
  if (typeof guide.doneKey !== 'string' || !guide.doneKey) {
    problems.push(`${guide.id}: needs a doneKey`);
  }
  if (typeof guide.descriptionKey !== 'string' || !guide.descriptionKey) {
    problems.push(`${guide.id}: needs a descriptionKey`);
  }
  if (![1, 2, 3, 4].includes(guide.tier)) {
    problems.push(`${guide.id}: tier must be 1, 2, 3 or 4`);
  }
  if (
    guide.finishRoutes !== undefined &&
    (!Array.isArray(guide.finishRoutes) ||
      guide.finishRoutes.some(name => typeof name !== 'string'))
  ) {
    problems.push(`${guide.id}: finishRoutes must be an array of route names`);
  }
  const seen = new Set();
  guide.steps.forEach((step, index) => {
    const label = `${guide.id} step ${index + 1}`;
    if (!step.id) problems.push(`${label}: missing id`);
    if (seen.has(step.id)) problems.push(`${label}: duplicate id ${step.id}`);
    seen.add(step.id);
    if (!Array.isArray(step.routes) || step.routes.length === 0) {
      problems.push(`${label}: needs a non-empty routes array`);
    }
    if (!step.titleKey || !step.bodyKey) {
      problems.push(`${label}: needs titleKey and bodyKey`);
    }
    if (step.placement && !PLACEMENTS.includes(step.placement)) {
      problems.push(`${label}: unknown placement ${step.placement}`);
    }
    if (
      step.routeParams !== undefined &&
      (typeof step.routeParams !== 'object' || step.routeParams === null)
    ) {
      problems.push(`${label}: routeParams must be an object`);
    }
    if (step.disabledKey !== undefined && !step.target) {
      problems.push(`${label}: disabledKey needs a target`);
    }
    if (step.action !== undefined && typeof step.action !== 'boolean') {
      problems.push(`${label}: action must be a boolean`);
    }
  });
  return problems;
};
